/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";
import Swal from 'sweetalert2';
import SeperatorSupport from "./seperatorSupport";
import SeperatorSupportDetails from "./seperatorDetails";
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';


// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

// Soft UI Dashboard PRO React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import { Modal, Box, Typography, Button } from '@mui/material';
import { getFirestore, getDocs, getDoc, doc, collection, query, where } from "firebase/firestore";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import SoftSelect from "components/SoftSelect";


// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Soft UI Dashboard PRO React context
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Images
import team2 from "assets/images/team-2.jpg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";
import { Tooltip } from "@mui/material";

function DashboardNavbar({ absolute, light, isMini, staticNavbar }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [openSupportModal, setOpenSupportModal] = useState(false);
  const [currentUserSubscription, setCurrentUserSubscription] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["About", "Profession"];
  const [userName, setUserName] = useState({ name: "", email: "" });
  const [email, setEmail] = useState("");
  const [problem, setProblem] = useState(null);
  const [problemDescription, setProblemDescription] = useState("");
  const [pageProblem, setPageProblem] = useState("");
  const [userNumber, setUserNumber] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSelectChange = (selectedOption) => {
    setProblem(selectedOption);
    console.log("problme", selectedOption)
  };

  const handleNext = () => setCurrentStep((prevStep) => prevStep + 1);
  const handleBack = () => setCurrentStep((prevStep) => prevStep - 1);


  {/*const stepComponents = [
    <StepOne />,
    <StepTwo />,
    <StepThree />, // Define these components as per your requirements
  ];*/}

  useEffect(() => {
    if (userName && userName.email && email === "") {
      setEmail(userName.email);
    }
  }, [userName, email]);

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
    if (user) {
        const fetchUserSubscription = async () => {
            try {
                const db = getFirestore();
                const userSubDoc = collection(db, 'customerDetails', user.uid, 'subscriptions'); 
                const q = query(userSubDoc, where("status", "in", ["active", "trialing"]));
                const querySnapshot = await getDocs(q);
                let latestSubscription = null;

                querySnapshot.forEach((doc) => {
                if (!latestSubscription || doc.data().current_period_start.seconds > latestSubscription.current_period_start.seconds) {
                    latestSubscription = doc.data();
                }
                });

                setCurrentUserSubscription(latestSubscription.items[0].price.product.name);
                console.log("user subscription: ", latestSubscription.items[0].price.product.name);
            } catch (error) {
                console.error("Error fetching user subscription:", error);
                setCurrentUserSubscription(null);
            }   
        };
        fetchUserSubscription();
    }
    });
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
        const fetchUserNames = async () => {
            try {
                const db = getFirestore();
                const userDocRef = doc(db, "users", user.uid);
                const userDoc = await getDoc(userDocRef);

                setUserName({
                  name: userDoc.data().firstname,
                  email: userDoc.data().email,
                });
                console.log("username: ", userDoc.data().email)
            } catch (error) {
                console.log(" could not retrieve user name")
            }   
        };
        fetchUserNames();
    }
    });
    return () => unsubscribe();
  }, []);

  

  const handleOpenSupportModal = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      setOpenSupportModal(true);
    } else {
      Swal.fire({
        title: 'Unauthorized',
        text: 'You need to be logged in to access support.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });  
    }
  };

  const submitSupportTicket = () => {
    // Ensure all necessary data is available
    if (!email || !problem || !problem.value) {
      Swal.fire({
        title: 'Missing Information',
        text: 'Please make sure all required fields are filled out.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }
  
    const supportTicketData = {
      email: email,
      problemType: problem.value,
      userName: userName.name,
      userTier: currentUserSubscription,
      description: problemDescription,
    };
  
    fetch('https://services.leadconnectorhq.com/hooks/scTZfdVrJb3bMVHNemTZ/webhook-trigger/cd9b3321-e4ee-424e-86fe-69f8467ead6f', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(supportTicketData),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Support ticket submission failed');
      }
      Swal.fire({
        title: 'Success!',
        text: 'Your support ticket has been submitted successfully.',
        icon: 'success',
        confirmButtonText: 'OK'
      });
      handleCloseSupportModal(); 
      setProblem(null);
      setProblemDescription("");
    })
    .catch((error) => {
      console.error('Support Ticket Submission Error:', error);
      Swal.fire({
        title: 'Error',
        text: 'There was an issue submitting your support ticket. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    });
  };
  
    

  const handleCloseSupportModal = () => setOpenSupportModal(false);


  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsSignedIn(!!user);  // Set true if user is signed in, else false
    });
  
    return () => unsubscribe();  // Cleanup subscription
  }, []);
  
  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    

  

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        image={<img src={team2} alt="person" />}
        title={["New message", "from Laur"]}
        date="13 minutes ago"
        onClick={handleCloseMenu}
      />
      <NotificationItem
        image={<img src={logoSpotify} alt="person" />}
        title={["New album", "by Travis Scott"]}
        date="1 day"
        onClick={handleCloseMenu}
      />
      <NotificationItem
        color="secondary"
        image={
          <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
            payment
          </Icon>
        }
        title={["", "Payment successfully completed"]}
        date="2 days"
        onClick={handleCloseMenu}
      />
    </Menu>
  );

  return (
    <AppBar
      position={staticNavbar ? "static" : (absolute ? "absolute" : navbarType)}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          <Icon fontSize="medium" sx={navbarDesktopMenu} onClick={handleMiniSidenav}>
            {miniSidenav ? "menu_open" : "menu"}
          </Icon>
        </SoftBox>
        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            <SoftBox pr={1}>
              <SoftInput
                placeholder="Type here..."
                icon={{ component: "search", direction: "left" }}
              />
            </SoftBox>
            <SoftBox pr={0} className="joyride-support-ticket">
              <Tooltip title="Get Support" placement="bottom" >
                <IconButton sx={navbarIconButton} size="small" onClick={handleMenuOpen}>
                  <Icon sx={({ palette: { dark, white } }) => ({ color: light ? white.main : dark.main })}>
                    help
                  </Icon>
                  <SoftTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                    Support
                  </SoftTypography>
                </IconButton>
              </Tooltip>
            </SoftBox>
            <SoftBox color={light ? "white" : "inherit"}>
              {isSignedIn ? (
                // If user is signed in, show "Account" and link to profile overview
              <Link to="/pages/profile-overview">
                <Tooltip title="Profile-Overview" placement="bottom" >
                  <IconButton sx={navbarIconButton} size="small">
                    <Icon sx={({ palette: { dark, white } }) => ({ color: light ? white.main : dark.main })}>
                      account_circle
                    </Icon>
                    <SoftTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                      Account
                    </SoftTypography>
                  </IconButton>
                </Tooltip>
              </Link>
              ) : (
              
              
              <Link to="/authentication/sign-in/basic">
                <IconButton sx={navbarIconButton} size="small">
                  <Icon
                    sx={({ palette: { dark, white } }) => ({
                      color: light ? white.main : dark.main,
                    })}
                  >
                    account_circle
                  </Icon>
                  <SoftTypography
                    variant="button"
                    fontWeight="medium"
                    color={light ? "white" : "dark"}
                  >
                    Sign in
                  </SoftTypography>
                </IconButton>
              </Link>
              )}
              <IconButton
                size="small"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <Tooltip title="Go to Website" placement="bottom" >
                <IconButton
                  size="small"
                  color="inherit"
                  sx={navbarIconButton}
                  
                  component="a"
                  href="https://www.optimind-ai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  //to="/applications/settings"
                >
                  < LanguageOutlinedIcon />
                </IconButton>
              </Tooltip>
              {/*<IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Icon className={light ? "text-white" : "text-dark"}>notifications</Icon>
                  </IconButton>*/}
              {renderMenu()}
            </SoftBox>
          </SoftBox>
        )}
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => { handleOpenSupportModal(); handleMenuClose(); }}>
            Start a Ticket
          </MenuItem>
          <MenuItem onClick={() => { window.open('https://calendly.com/woutvanparijs/30min', '_blank', 'noopener,noreferrer'); handleMenuClose(); }}>
            Onboarding Call
          </MenuItem>
        </Menu>
        <Modal
          open={openSupportModal}
          onClose={handleCloseSupportModal}
          aria-labelledby="support-modal-title"
          aria-describedby="support-modal-description"
        >
          <SoftBox
            sx={{
              position: 'absolute',
              maxHeight: '95vh',
              top: '50%',
              left: '55%',
              transform: 'translate(-50%, -50%)',
              width: 800,
              bgcolor: 'background.paper',
              //border: '1px solid #000',
              borderRadius: '8px',
              boxShadow: 24,
              p: 4,
              overflowY: 'auto',
            }}
          >
            <Tooltip title="Closes the support form and loses all content" placement="bottom" >
              <SoftButton
                sx={{
                  position: 'absolute',
                  right: 15, 
                  top: 15, 
                }}
                color="dark" 
                variant="outlined"
                onClick={handleCloseSupportModal} 
              >
                Close 
              </SoftButton>
            </Tooltip>
            <SoftBox mt={2} mb={4} display="flex" justifyContent="center" alignItems="center" flexDirection="column" >
              <SoftTypography id="support-modal-title" variant="h5" >
                We are happy to help {userName.name}!
              </SoftTypography>
              <SoftTypography textAlign="center" variant="body2" color="text" sx={{ mt: 1 }}>
                Please fill in the details below to start a support ticket. Once you are done, simply submit the ticket and we will get back to you asap.
              </SoftTypography>
            </SoftBox>
            < SeperatorSupport />
            {/*<Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
              </Stepper>*/}

            <SoftBox flexDirection="column" justifyContent="flex-start" alignItems="flex-start" mt={2}>
              <SoftBox pb={0.5} >
                <SoftBadge
                    variant="contained"
                    color="warning"
                    size="md"
                    badgeContent={
                      < SoftTypography variant="h6" >Your Support Tier: </SoftTypography>
                    }
                    //circular
                    container
                />
                <Tooltip title="This is your support tier based on your subscription package" placement="top" >
                    <SoftTypography variant="button" fontSize="16px" fontWeight="bold" ml={1.5} >{currentUserSubscription === "Gold Subscription" ? "Premium Support" : currentUserSubscription === "Platinum Subscription" ? "Ultimate Support" : currentUserSubscription === "Silver Subscription" ? "Basic Support" : "No Subscription"}</SoftTypography>
                </Tooltip>
              </SoftBox>
              <SoftBox pb={6} pl={1} >
                <SoftTypography variant="button" color="text" >
                  {currentUserSubscription === "Platinum Subscription" ? "Based on your support tier, it will take us 1-2 hours on average to answer your ticket." : currentUserSubscription === "Gold Subscription" ? "Based on your support tier, it will take us 2-3 hours on average to answer your ticket." : currentUserSubscription === "Silver Subscription" ? "Based on your support tier, it will take us 3-4 hours on average to answer your ticket." : "You are not eligable for our support system, if you have any questions, please message us on: 'support@optimind-ai.com'"}
                </SoftTypography>
              </SoftBox>
              < SeperatorSupportDetails />

              <SoftBox flex={1} pl={1}>
                  <SoftTypography variant="button" fontWeight="bold" ml={0.7} mb={1}>Email to contact you*</SoftTypography>
                  <SoftInput
                  placeholder="your_email@example.com"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  />
              </SoftBox>
              <SoftBox mt={2} flex={1} pl={1}>
                <SoftTypography variant="button" fontWeight="bold" ml={0.7} mb={1}>Select the type of support you need*</SoftTypography>
                <SoftSelect 
                  placeholder="Product/Service Information"
                  value={problem}
                  onChange={handleSelectChange}
                  options={[
                    { value: "Technical Support", label: "Technical Support"},
                    { value: "Account & Billing Support", label: "Account & Billing Support"},
                    { value: "Product/Service Information", label: "Product/Service Information"},
                  ]}
                  isClearable={true}
                  //menuPortalTarget={document.body} // Render the dropdown menu as a portal appended to the body
                  //styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} // Adjust z-index as needed
                />
              </SoftBox>
              { problem && problem.value === "Technical Support" && (
                <SoftBox mt={2} flex={1} pl={1}>
                  <SoftTypography variant="button" fontWeight="bold" ml={0.7} mb={1}>Please describe your problem*</SoftTypography>
                  <SoftInput
                    placeholder=""
                    multiline
                    rows={4}
                    fullWidth
                    value={problemDescription}
                    onChange={(e) => setProblemDescription(e.target.value)}
                  />
                </SoftBox>
              )}

              { problem && problem.value === "Account & Billing Support" && (
                <SoftBox mt={2} flex={1} pl={1}>
                  <SoftTypography variant="button" fontWeight="bold" ml={0.7} mb={1}>Please describe your problem*</SoftTypography>
                  <SoftInput
                    placeholder=""
                    multiline
                    rows={4}
                    fullWidth
                    value={problemDescription}
                    onChange={(e) => setProblemDescription(e.target.value)}
                  />
                </SoftBox>
              )}

              { problem && problem.value === "Product/Service Information" && (
                <SoftBox mt={2} flex={1} pl={1}>
                  <SoftTypography variant="button" fontWeight="bold" ml={0.7} mb={1}>Please describe your problem*</SoftTypography>
                  <SoftInput
                    placeholder=""
                    multiline
                    rows={4}
                    fullWidth
                    value={problemDescription}
                    onChange={(e) => setProblemDescription(e.target.value)}
                  />
                </SoftBox>
              )}
              <SoftBox mt={2} flex={1} pl={1}>
                <SoftTypography variant="button" fontWeight="bold" ml={0.7} mb={1}>Number to contact you</SoftTypography>
                <SoftInput
                  placeholder=""
                  fullWidth
                  value={userNumber}
                  onChange={(e) => setUserNumber(e.target.value)}
                />
                <SoftTypography variant="button" color="text" fontWeight="bold" ml={0.7} mb={1}>Only provide your number if you prefer to be called!</SoftTypography>
              </SoftBox>

            </SoftBox>
            <SoftBox display="flex" justifyContent="center" pt={4} >
              <Tooltip title="Submit your ticket" placement="top" >
                <SoftButton color="dark" startIcon={ <SendOutlinedIcon sx={{mr:1}} />} onClick={submitSupportTicket}>Submit ticket</SoftButton>
              </Tooltip>
            </SoftBox>
          </SoftBox>
        </Modal>
      </Toolbar>
    </AppBar>
    
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  staticNavbar: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  staticNavbar: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
